/**
 * GoogleShoppingAdMockup Component
 *
 * Google Shopping product listing ad mockup
 * Shows product image, title, price, and store name
 */

import React from 'react';
import { stripMediaTags } from '@/utils/mediaTagParser';
import { AdMockupContainer } from '../base/AdMockupContainer';
import { GoogleShoppingAdMockupProps } from '../types/platform.types';

export const GoogleShoppingAdMockup: React.FC<GoogleShoppingAdMockupProps> = ({
  productTitle,
  price,
  storeName,
  imageUrl,
  rating,
  freeShipping = false,
}) => {
  return (
    <AdMockupContainer platform="google_shopping">
      <div className="bg-white dark:bg-card-darkAlt rounded-lg border border-gray-200 dark:border-border-dark overflow-hidden shadow-sm max-w-[220px] mx-auto">

        {/* Product Image */}
        <div className="relative bg-gray-50 dark:bg-gray-800">
          {imageUrl ? (
            <div className="aspect-square">
              <img
                src={imageUrl}
                alt={productTitle}
                className="w-full h-full object-contain p-2"
              />
            </div>
          ) : (
            <div className="aspect-square flex items-center justify-center">
              <svg className="w-12 h-12 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
          )}

          {/* Sponsored label */}
          <div className="absolute top-1.5 left-1.5">
            <span className="px-1 py-0.5 bg-white/90 dark:bg-gray-800/90 rounded text-[9px] font-medium text-gray-500 dark:text-gray-400">
              Sponsored
            </span>
          </div>
        </div>

        {/* Product Details */}
        <div className="p-2.5">
          {/* Product Title */}
          <h4 className="text-xs font-medium text-gray-900 dark:text-white line-clamp-2 mb-1 leading-tight">
            {stripMediaTags(productTitle)}
          </h4>

          {/* Price */}
          <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">
            {price}
          </p>

          {/* Store Name */}
          <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
            {storeName}
          </p>

          {/* Rating */}
          {rating != null && rating > 0 && (
            <div className="flex items-center gap-1 mt-1">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg
                    key={star}
                    className={`w-3 h-3 ${
                      star <= Math.round(rating)
                        ? 'text-yellow-400'
                        : 'text-gray-300 dark:text-gray-600'
                    }`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                {rating.toFixed(1)}
              </span>
            </div>
          )}

          {/* Free Shipping Badge */}
          {freeShipping && (
            <div className="mt-1.5">
              <span className="text-[10px] text-green-600 dark:text-green-400 font-medium">
                Free shipping
              </span>
            </div>
          )}
        </div>
      </div>
    </AdMockupContainer>
  );
};

export default GoogleShoppingAdMockup;
